import { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import MobileHeader from './MobileHeader';
import MobileBottomNav from './MobileBottomNav';
import MobileDrawer from '../layout/MobileDrawer';
import useUIStore from '../../store/uiStore';

// ---------------------------------------------------------------------------
// Routes that get full-screen treatment (no header, no bottom nav)
// The viewer needs 100% of available viewport — any chrome steals space and
// causes the blank-area bug in Capacitor where the WebView height includes
// the status bar but the CSS doesn't.
// ---------------------------------------------------------------------------
const FULLSCREEN_ROUTES = [
  // Add routes here that should be truly fullscreen (no header/footer)
  // e.g. '/viewer/', '/scan/'
];

// Routes where we want the header visible but NOT the bottom nav
// (detail pages — saves vertical space for content)
const NO_BOTTOM_NAV_ROUTES = [
  '/documents/',  // document detail (starts-with match)
  '/pages/',      // page editor
];

function isFullscreen(pathname) {
  return FULLSCREEN_ROUTES.some((r) => pathname.startsWith(r));
}

function hideBottomNav(pathname) {
  return NO_BOTTOM_NAV_ROUTES.some((r) => pathname.startsWith(r));
}

export default function MobileLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const prevPathRef = useRef(location.pathname);
  const { theme } = useUIStore();
  const fullscreen = isFullscreen(location.pathname);
  const noBottomNav = hideBottomNav(location.pathname) || fullscreen;

  // Apply theme
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  // Close drawer on route change
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      setDrawerOpen(false);
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname]);

  // ---------------------------------------------------------------------------
  // CRITICAL FIX: 100vh is broken in Android WebView and iOS Safari.
  // On Android, 100vh includes the space behind the navigation bar.
  // Fix: use the visual viewport API to set a --app-height CSS variable,
  // then use that instead of 100vh everywhere.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const setAppHeight = () => {
      // visualViewport accounts for keyboard, status bar, nav bar correctly
      const h = window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight;
      document.documentElement.style.setProperty('--app-height', `${h}px`);
    };

    setAppHeight();

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', setAppHeight);
      window.visualViewport.addEventListener('scroll', setAppHeight);
    } else {
      window.addEventListener('resize', setAppHeight, { passive: true });
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', setAppHeight);
        window.visualViewport.removeEventListener('scroll', setAppHeight);
      } else {
        window.removeEventListener('resize', setAppHeight);
      }
    };
  }, []);

  // Determine back button and title
  const showBack =
    location.pathname !== '/dashboard' &&
    location.pathname !== '/documents' &&
    location.pathname !== '/upload' &&
    location.pathname !== '/settings';

  const getTitle = () => {
    const path = location.pathname;
    if (path.startsWith('/documents/') && path.includes('/crop')) return 'Crop Document';
    if (path.startsWith('/documents/')) return 'Document';
    if (path.startsWith('/pages/')) return 'Edit Page';
    if (path === '/document-types') return 'Document Types';
    if (path === '/api-keys') return 'API Keys';
    if (path === '/api-tokens') return 'API Tokens';
    if (path === '/users') return 'Users';
    if (path === '/fine-tune') return 'Fine-Tune';
    if (path === '/statistics') return 'Statistics';
    if (path === '/admin-api-docs') return 'Admin API Docs';
    return null;
  };

  const handleBack = () => navigate(-1);

  // ---------------------------------------------------------------------------
  // Layout structure:
  //
  // <div.mobile-shell>          ← height: var(--app-height), overflow: hidden
  //   <MobileHeader />          ← fixed, 56px, z-50
  //   <main.mobile-content>     ← flex-1, overflow-y-auto, -webkit-overflow-scrolling: touch
  //     <Outlet />              ← page content
  //   </main>
  //   <MobileBottomNav />       ← fixed, 64px + safe-area-bottom, z-50
  // </div>
  //
  // The shell is position:fixed with --app-height so Capacitor WebView sees
  // the correct height. The header and nav are also fixed so they don't
  // participate in the scroll container. The main area gets the exact remaining
  // space via padding offsets.
  //
  // For document detail: pt-14 (header) + pb-0 (no bottom nav wasted space,
  // the viewer manages its own safe-area padding at the bottom action bar).
  // ---------------------------------------------------------------------------

  const mainPaddingTop = fullscreen ? '0px' : '56px'; // header height
  const mainPaddingBottom = fullscreen ? '0px' : noBottomNav ? '0px' : '80px'; // bottom nav height

  return (
    <div
      className="mobile-shell bg-theme-primary"
      style={{
        position: 'fixed',
        inset: 0,
        height: 'var(--app-height, 100dvh)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      {!fullscreen && (
        <MobileHeader
          onMenuOpen={() => setDrawerOpen(true)}
          title={getTitle()}
          showBack={showBack}
          onBack={handleBack}
        />
      )}

      {/* Main Content — the only scrolling container */}
      <main
        className="mobile-content"
        style={{
          position: 'absolute',
          top: mainPaddingTop,
          bottom: mainPaddingBottom,
          left: 0,
          right: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          // Prevent content from going under safe areas on notched devices
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Bottom Navigation */}
      {!noBottomNav && (
        <MobileBottomNav onMoreClick={() => setDrawerOpen(true)} />
      )}

      {/* Drawer */}
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </div>
  );
}

